// Tools
import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { Route, Routes, useNavigate } from "react-router-dom";
import gsap from "gsap";
// Styles
import "./main.scss";
// Components
import { Header } from "./components/header/Header";
import { Workspace } from "./components/workspace/Workspace";
import { CampaignDetailPage } from "../campaign-detail/CampaignDetailPage";
import { CopyToast } from "../../shared/components/copy-toast/CopyToast";
import { CircleLoader } from "../../shared/components/circle-loader/CircleLoader";
// Hooks
import { useGlobalCopyOnClick } from "../../shared/hooks/useGlobalCopyOnClick";
// Service
import { getAuthData } from "./service/auth-me.service";
import wsClient from "../../shared/websocket/core/wsClient";
import { registerWsSessionRebind } from "../../shared/websocket/core/wsSessionRebind";
import "../../shared/websocket/events/socketEvents";
// Utils
import { hideElement } from "../../shared/ui/gsap-animations/hidden-element";
// Actions
import { setUserData } from "../../shared/store/slices/user.slice";
import { SPLIT_PANE_INTERACTIVE_MIN_WIDTH_PX } from "./components/workspace/utils/split-pane/use-split-pane-layout-interactive";

const SELECTABLE_ON_MOBILE =
	"textarea, input, select, option, [contenteditable], .window__body, .window__body_list-inner, [data-copyable], [data-copy-text], .header-modal, .copy-toast";

export const Main = () => {
	// ---------- Dispatch
	const dispatch = useDispatch();
	// ---------- Navigation
	const navigate = useNavigate();
	// ---------- Refs
	const authLoaderRef = useRef<HTMLDivElement>(null);
	// ---------- State
	const [copyToast, setCopyToast] = useState<{
		visible: boolean;
		text: string;
	}>({
		visible: false,
		text: "",
	});

	// ---------- Effects
	// Вызываем userData один раз при монтировании компонента
	useEffect(() => {
		let mounted = true;
		const fetchAuth = async () => {
			try {
				const response = await getAuthData();
				if (!mounted) return;
				if (!response.success) {
					navigate("/auth");
				}
				if (response.data) {
					dispatch(setUserData(response.data));
				}
				// Повторная привязка WS-сокета к сессии (DAI-1183): после
				// обновления страницы auth уходит без session_id — подписка
				// переотправляет auth при смене sessionId на не-null
				registerWsSessionRebind();
				// Открывает Вебсокет соединение
				wsClient.connect();
				hideElement(authLoaderRef.current as HTMLElement);
			} catch (err) {
				console.error("getAuthData failed:", err);
			}
		};
		void fetchAuth();
		return () => {
			mounted = false;
		};
	}, []);

	// Скрываем тост через 1 секунду после появления
	useEffect(() => {
		if (!copyToast.visible) return;
		const id = window.setTimeout(() => {
			setCopyToast((prev) => ({ ...prev, visible: false }));
		}, 1000);
		return () => window.clearTimeout(id);
	}, [copyToast.visible]);

	// ---------- Functions
	// Копирование текста по клику
	useGlobalCopyOnClick((text) => {
		setCopyToast({ visible: true, text });
	});

	// iOS/Android WebKit часто игнорирует user-select на предках — гасим selectstart по каркасу
	useEffect(() => {
		const mq = window.matchMedia(
			`(max-width: ${SPLIT_PANE_INTERACTIVE_MIN_WIDTH_PX}px)`,
		);

		const onSelectStart = (e: Event) => {
			if (!mq.matches) return;

			const t = e.target;
			const el =
				t instanceof Element
					? t
					: t instanceof Text
						? t.parentElement
						: null;
			if (!el || !el.closest(SELECTABLE_ON_MOBILE)) {
				e.preventDefault();
			}
		};

		document.addEventListener("selectstart", onSelectStart, { capture: true });
		return () => {
			document.removeEventListener("selectstart", onSelectStart, {
				capture: true,
			});
		};
	}, []);

	return (
		<div className='main'>
			<div ref={authLoaderRef} className='main__auth-loader'>
				<div className='main__auth-loader-content'>
					<CircleLoader />
				</div>
			</div>
			<Routes>
				<Route
					index
					element={
						<>
							<Header />
							<Workspace />
						</>
					}
				/>
				<Route path='campaign/:campaignId' element={<CampaignDetailPage />} />
			</Routes>
			<CopyToast visible={copyToast.visible} text={copyToast.text} />
		</div>
	);
};
